import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Save, X} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import { lumi } from "@/lib/lumi"

interface CadastroGeralProps {
  editingId?: string
  onSaveComplete?: () => void
}

export default function CadastroGeral({ editingId, onSaveComplete }: CadastroGeralProps) {
  const { logoUrl } = useGuias()
  const [formData, setFormData] = useState({
    nomeCompleto: "",
    cpfCnpj: "",
    telefone: "",
    endereco: "",
    cep: "",
    email: ""
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (editingId) {
      loadCadastro()
    }
  }, [editingId])

  const loadCadastro = async () => {
    try {
      const cadastro = await lumi.entities.cadastro_geral.retrieve(editingId!)
      setFormData({
        nomeCompleto: cadastro.nomeCompleto || "",
        cpfCnpj: cadastro.cpfCnpj || "",
        telefone: cadastro.telefone || "",
        endereco: cadastro.endereco || "",
        cep: cadastro.cep || "",
        email: cadastro.email || ""
      })
    } catch (error) {
      console.error("Erro ao carregar cadastro:", error)
    }
  }

  const formatCPFCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    
    if (numbers.length <= 11) {
      // Formatar CPF: 000.000.000-00
      return numbers
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    } else {
      // Formatar CNPJ: 00.000.000/0000-00
      return numbers
        .replace(/(\d{2})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1/$2")
        .replace(/(\d{4})(\d{1,2})$/, "$1-$2")
    }
  }

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    return numbers.replace(/(\d{5})(\d{1,3})$/, "$1-$2")
  }

  const formatTelefone = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{1,4})$/, "($1) $2-$3")
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{1,4})$/, "($1) $2-$3")
  }

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return regex.test(email)
  }

  const validateCPF = (cpf: string) => {
    const numbers = cpf.replace(/\D/g, "")
    if (numbers.length !== 11) return false
    
    if (/^(\d)\1+$/.test(numbers)) return false
    
    let sum = 0
    for (let i = 0; i < 9; i++) {
      sum += parseInt(numbers.charAt(i)) * (10 - i)
    }
    let digit = 11 - (sum % 11)
    if (digit >= 10) digit = 0
    if (digit !== parseInt(numbers.charAt(9))) return false
    
    sum = 0
    for (let i = 0; i < 10; i++) {
      sum += parseInt(numbers.charAt(i)) * (11 - i)
    }
    digit = 11 - (sum % 11)
    if (digit >= 10) digit = 0
    if (digit !== parseInt(numbers.charAt(10))) return false
    
    return true
  }

  const validateCNPJ = (cnpj: string) => {
    const numbers = cnpj.replace(/\D/g, "")
    if (numbers.length !== 14) return false
    
    if (/^(\d)\1+$/.test(numbers)) return false
    
    let sum = 0
    let weight = 5
    for (let i = 0; i < 12; i++) {
      sum += parseInt(numbers.charAt(i)) * weight
      weight = weight === 2 ? 9 : weight - 1
    }
    let digit = sum % 11 < 2 ? 0 : 11 - (sum % 11)
    if (digit !== parseInt(numbers.charAt(12))) return false
    
    sum = 0
    weight = 6
    for (let i = 0; i < 13; i++) {
      sum += parseInt(numbers.charAt(i)) * weight
      weight = weight === 2 ? 9 : weight - 1
    }
    digit = sum % 11 < 2 ? 0 : 11 - (sum % 11)
    if (digit !== parseInt(numbers.charAt(13))) return false
    
    return true
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === "cpfCnpj") {
      formattedValue = formatCPFCNPJ(value)
    } else if (name === "cep") {
      formattedValue = formatCEP(value)
    } else if (name === "telefone") {
      formattedValue = formatTelefone(value)
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }))
    setErrors(prev => ({ ...prev, [name]: "" }))
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.nomeCompleto.trim()) {
      newErrors.nomeCompleto = "Nome completo é obrigatório"
    }

    if (!formData.cpfCnpj.trim()) {
      newErrors.cpfCnpj = "CPF/CNPJ é obrigatório"
    } else {
      const numbers = formData.cpfCnpj.replace(/\D/g, "")
      if (numbers.length === 11) {
        if (!validateCPF(formData.cpfCnpj)) {
          newErrors.cpfCnpj = "CPF inválido"
        }
      } else if (numbers.length === 14) {
        if (!validateCNPJ(formData.cpfCnpj)) {
          newErrors.cpfCnpj = "CNPJ inválido"
        }
      } else {
        newErrors.cpfCnpj = "CPF/CNPJ inválido"
      }
    }

    if (!formData.telefone.trim()) {
      newErrors.telefone = "Telefone é obrigatório"
    }

    if (!formData.endereco.trim()) {
      newErrors.endereco = "Endereço é obrigatório"
    }

    if (!formData.cep.trim()) {
      newErrors.cep = "CEP é obrigatório"
    } else if (formData.cep.replace(/\D/g, "").length !== 8) {
      newErrors.cep = "CEP inválido"
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email é obrigatório"
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Email inválido"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setLoading(true)
    try {
      const cadastroData = {
        ...formData,
        creator: "admin",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      if (editingId) {
        await lumi.entities.cadastro_geral.update(editingId, cadastroData)
        alert("Cadastro atualizado com sucesso!")
      } else {
        await lumi.entities.cadastro_geral.create(cadastroData)
        alert("Cadastro criado com sucesso!")
      }

      setFormData({
        nomeCompleto: "",
        cpfCnpj: "",
        telefone: "",
        endereco: "",
        cep: "",
        email: ""
      })

      if (onSaveComplete) {
        onSaveComplete()
      }
    } catch (error) {
      console.error("Erro ao salvar cadastro:", error)
      alert("Erro ao salvar cadastro")
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      nomeCompleto: "",
      cpfCnpj: "",
      telefone: "",
      endereco: "",
      cep: "",
      email: ""
    })
    setErrors({})
    if (onSaveComplete) {
      onSaveComplete()
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
        <div className="flex items-center gap-4 mb-6">
          <img src={logoUrl} alt="Logo" className="h-16 w-auto" />
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {editingId ? "Editar Cadastro Geral" : "Novo Cadastro Geral"}
            </h2>
            <p className="text-sm text-gray-500">Preencha os dados abaixo</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome Completo ou Nome da Empresa *
              </label>
              <input
                type="text"
                name="nomeCompleto"
                value={formData.nomeCompleto}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent ${
                  errors.nomeCompleto ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Digite o nome completo ou razão social"
              />
              {errors.nomeCompleto && (
                <p className="text-red-500 text-sm mt-1">{errors.nomeCompleto}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF ou CNPJ *
              </label>
              <input
                type="text"
                name="cpfCnpj"
                value={formData.cpfCnpj}
                onChange={handleChange}
                maxLength={18}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent ${
                  errors.cpfCnpj ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
              />
              {errors.cpfCnpj && (
                <p className="text-red-500 text-sm mt-1">{errors.cpfCnpj}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefone *
              </label>
              <input
                type="text"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                maxLength={15}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent ${
                  errors.telefone ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="(00) 00000-0000"
              />
              {errors.telefone && (
                <p className="text-red-500 text-sm mt-1">{errors.telefone}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Endereço *
              </label>
              <input
                type="text"
                name="endereco"
                value={formData.endereco}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent ${
                  errors.endereco ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Rua, número, bairro, cidade - UF"
              />
              {errors.endereco && (
                <p className="text-red-500 text-sm mt-1">{errors.endereco}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CEP *
              </label>
              <input
                type="text"
                name="cep"
                value={formData.cep}
                onChange={handleChange}
                maxLength={9}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent ${
                  errors.cep ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="00000-000"
              />
              {errors.cep && (
                <p className="text-red-500 text-sm mt-1">{errors.cep}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent ${
                  errors.email ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="exemplo@email.com"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 bg-gradient-to-r from-[#004161] to-[#005580] text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? "Salvando..." : editingId ? "Atualizar" : "Salvar"}
            </motion.button>
            <motion.button
              type="button"
              onClick={handleCancel}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" />
              Cancelar
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
  )
}
